import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { addEmployerSavedCandidate, fetchEmployerSavedCandidate, removeEmployerSavedCandidate } from "./EmployerSaveCandidateAPI"

export const addEmployerSavedCandidateAsync = createAsyncThunk(
    "EmployerSavedCandidate/add",
    async ({ candidate_uuid }, { rejectWithValue }) => {
        try {
            const data = await addEmployerSavedCandidate(candidate_uuid)
            return { ...data.response, candidate_uuid } // Include the UUID in the response
        } catch (error) {
            return rejectWithValue(error.message)
        }
    },
)
export const removeEmployerSavedCandidateAsync = createAsyncThunk(
    "EmployerSavedCandidate/remove",
    async ({ candidate_uuid }, { rejectWithValue }) => {
        try {
            const data = await removeEmployerSavedCandidate(candidate_uuid)
            return { ...data.response, candidate_uuid }
        } catch (error) {
            return rejectWithValue(error.message)
        }
    },
)

export const fetchEmployerSavedCandidateAsync = createAsyncThunk(
    "EmployerSavedCandidate/fetch",
    async ({ page = 1, perPage = 10 } = {}, { rejectWithValue }) => {
        try {
            const data = await fetchEmployerSavedCandidate(page, perPage)
            return data?.response
        } catch (error) {
            return rejectWithValue(error.message)
        }
    },
)

const initialState = {
    data: [],
    loading: false,
    error: null,
    pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
    },
    savedCandidateUuids: [], // Track saved candidate UUIDs for quick lookup
}

const EmployerSavedCandidateSlice = createSlice({
    name: "EmployerSavedCandidate",
    initialState,
    reducers: {
        // Add a reducer to manually add a candidate UUID to the saved list
        addSavedCandidateUuid: (state, action) => {
            const uuid = action.payload
            if (!state.savedCandidateUuids.includes(uuid)) {
                state.savedCandidateUuids.push(uuid)
            }
        },
        // Add a reducer to remove a candidate UUID from the saved list
        removeSavedCandidateUuid: (state, action) => {
            const uuid = action.payload
            state.savedCandidateUuids = state.savedCandidateUuids.filter((id) => id !== uuid)
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch saved candidates
            .addCase(fetchEmployerSavedCandidateAsync.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchEmployerSavedCandidateAsync.fulfilled, (state, action) => {
                state.loading = false
                state.error = null
                state.data = action.payload

                // Extract and store candidate UUIDs for quick lookup
                if (action.payload) {
                    let candidates = []

                    // Handle different response structures
                    if (Array.isArray(action.payload)) {
                        candidates = action.payload
                    } else if (action.payload.data && Array.isArray(action.payload.data)) {
                        candidates = action.payload.data
                        // Update pagination if available
                        if (action.payload.pagination) {
                            state.pagination = action.payload.pagination
                        }
                    } else if (action.payload.records && Array.isArray(action.payload.records)) {
                        candidates = action.payload.records
                    }

                    // Extract UUIDs from candidates
                    state.savedCandidateUuids = candidates.map((item) => item.candidate?.uuid || item.uuid).filter(Boolean)
                }
            })
            .addCase(fetchEmployerSavedCandidateAsync.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload || action.error.message
            })

            // Add saved candidate
            .addCase(addEmployerSavedCandidateAsync.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(addEmployerSavedCandidateAsync.fulfilled, (state, action) => {
                state.loading = false
                state.error = null

                // Add the candidate UUID to the saved list immediately
                const candidateUuid = action.payload?.candidate_uuid || action.meta.arg.candidate_uuid
                if (candidateUuid && !state.savedCandidateUuids.includes(candidateUuid)) {
                    state.savedCandidateUuids.push(candidateUuid)
                }
            })
            .addCase(addEmployerSavedCandidateAsync.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload || action.error.message
            })

            // Remove saved candidate
            .addCase(removeEmployerSavedCandidateAsync.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(removeEmployerSavedCandidateAsync.fulfilled, (state, action) => {
                state.loading = false
                state.error = null

                // Remove the candidate UUID from the saved list immediately
                const candidateUuid = action.payload?.candidate_uuid || action.meta.arg.candidate_uuid
                if (candidateUuid) {
                    state.savedCandidateUuids = state.savedCandidateUuids.filter((id) => id !== candidateUuid)
                }
            })
            .addCase(removeEmployerSavedCandidateAsync.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload || action.error.message
            })
    },
})

export const { addSavedCandidateUuid, removeSavedCandidateUuid } = EmployerSavedCandidateSlice.actions

export const selectEmployerSavedCandidate = (state) => state.employerSavedCandidate
export const selectSavedCandidatePagination = (state) => state.employerSavedCandidate.pagination
export const selectSavedCandidateUuids = (state) => state.employerSavedCandidate.savedCandidateUuids
export const selectIsCandidateSaved = (candidateUuid) => (state) =>
    state.employerSavedCandidate.savedCandidateUuids.includes(candidateUuid)

export default EmployerSavedCandidateSlice.reducer